export default (client) => {
  return {
    uploadFile(file, onUploadProgress = function () {}, cancelToken = null) {
      const formData = new FormData()
      formData.append('file', file)

      const config = {
        cancelToken,
        onUploadProgress,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }

      return client.post('/user-files/upload-file/', formData, config)
    },
    uploadViaURL(url) {
      return client.post('/user-files/upload-via-url/', { url })
    },
    generateThumbnails(paths) {
      return client.post('/user-files/thumbnails/', { paths })
    },
    getScanStatus(name) {
      return client.get(`/user-files/${encodeURIComponent(name)}/scan-status/`)
    },
    regenerateOfficePreview({ tableId, rowId, fieldId, fileName }) {
      return client.post('/database/file-preview/regenerate/', {
        table_id: tableId,
        row_id: rowId,
        field_id: fieldId,
        file_name: fileName,
      })
    },
    /**
     * Reads the beginning of a file as text, for the preview of text and code
     * files.
     *
     * Read straight from the media url rather than through an endpoint of our
     * own: the url already carries the media signature and its expiry, so the
     * file passes the same gate it would for a download, and no second way out
     * of the instance has to exist for bytes. On object storage this needs a
     * bucket cors rule; without one the fetch fails and the preview falls back
     * to the file icon, which is what it did before there was a text preview.
     *
     * `Range` asks for the first bytes, but the answer is capped here as well —
     * a server may ignore the header and send a two gigabyte log file in full.
     *
     * @param {string} url the media url of the file.
     * @param {number} maxBytes how much to read at most.
     * @returns {Promise<{text: string, truncated: boolean}|null>} null when the
     *   file could not be read or is not text after all.
     */
    async getTextPreview(url, maxBytes) {
      const controller = new AbortController()
      try {
        const response = await fetch(url, {
          headers: { Range: `bytes=0-${maxBytes - 1}` },
          signal: controller.signal,
        })
        if (!response.ok || !response.body) {
          return null
        }

        const reader = response.body.getReader()
        const chunks = []
        let read = 0
        let truncated = false
        while (read < maxBytes) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          chunks.push(value)
          read += value.length
        }
        if (read >= maxBytes) {
          truncated = true
          // Nothing beyond the cap is wanted, so stop the transfer instead of
          // letting the rest arrive unread.
          controller.abort()
        }

        const bytes = new Uint8Array(read)
        let offset = 0
        for (const chunk of chunks) {
          bytes.set(chunk, offset)
          offset += chunk.length
        }
        const text = new TextDecoder('utf-8').decode(bytes.slice(0, maxBytes))

        // A file can carry a text extension and hold something else entirely. A
        // null byte is the giveaway, and showing a wall of replacement
        // characters helps nobody, so this is treated as "not previewable".
        if (text.includes('\u0000')) {
          return null
        }

        return { text, truncated }
      } catch (error) {
        // A blocked request (cors), an aborted one, a gone file: the preview has
        // one answer for all of them, its fallback.
        return null
      }
    },
    async getFileMetadata(url) {
      try {
        const response = await fetch(url, {
          method: 'HEAD',
        })
        const contentType = response.headers.get('content-type')
        const size = response.headers.get('content-length')
        return { contentType, size }
      } catch (e) {
        return { contentType: 'application/octet-stream', size: 0 }
      }
    },
  }
}
